import { useCallback, useEffect, useMemo, useState, type ChangeEvent } from 'react';
import { listCampaigns } from '../../api/campaigns';
import { listOpenPositions, listPositions, type OpenPositionRow } from '../../api/positions';
import { searchWorkers, type WorkerSearchResult } from '../../api/workers';
import {
  bulkAssign, createAssignment, listAssignedPositions, listWorkerAssignments,
  desasignarTurno, removeProposal, replaceAssignment,
  type AssignedPositionRow, type BulkAssignResult,
} from '../../api/assignments';
import { logAction } from '../../api/audit';
import { findOverlaps, rangesOverlap, type TimeRange } from '../../domain/overlap';
import { avisosDeReemplazo, puedeCambiarRecurso } from '../../domain/replacement';
import {
  FILTROS_VACIOS, formatoHorario, hayFiltros, opcionesDe, pasaFiltro,
  type FilaFiltrable, type FiltrosTabla,
} from '../../domain/tableFilters';
import {
  buildAssignmentsTemplate, parseAssignmentsFile,
  type ParsedAssignmentRow, type ParseResult,
} from '../../domain/excel/assignmentsTemplate';
import type { CampaignRow } from '../../lib/db-types';
import FilaTarjeta from '../../components/FilaTarjeta';
import BarraFiltros, {
  FiltroFecha, FiltroSelect, FiltroTexto, type OpcionFiltro,
} from '../../components/BarraFiltros';
import TarjetasResumen from '../../components/TarjetasResumen';
import { useAuth } from '../../components/AuthProvider';
import { puedeEditar } from '../../domain/permissions';

/**
 * Tres pestañas, no cuatro: "Propuestas" **se fusionó** en "Asignadas" (2026-08-11).
 * Listaba un subconjunto de lo mismo —solo las `propuesta`— y lo único que la distinguía era
 * el botón "Retirar", que ahora vive en la fila. Una propuesta aparecía en dos pestañas y en
 * ninguna se podía aprobar (eso es del jefe de operaciones, en Aprobaciones), así que la
 * división no ayudaba a decidir nada.
 */
type Tab = 'abiertas' | 'asignadas' | 'masiva';

/**
 * `soloEscritura` marca las solapas que NO tienen nada que mirar: existen para escribir.
 *
 * El validador de datos entra a esta pantalla en modo lectura (10-set), y una solapa «Carga
 * masiva» que solo sirve para subir un Excel de asignaciones no es una vista: es un formulario que
 * el servidor le va a rechazar. Las otras dos sí son vistas —la programación abierta y la
 * asignada—, que es exactamente el cruce que él necesita.
 */
const TAB_LABELS: { key: Tab; label: string; soloEscritura?: boolean }[] = [
  { key: 'abiertas', label: 'Posiciones abiertas' },
  { key: 'asignadas', label: 'Asignadas' },
  { key: 'masiva', label: 'Carga masiva', soloEscritura: true },
];

const hhmm = (t: string) => t.slice(0, 5);

/**
 * Opciones de tienda para el desplegable, sacadas de las filas **sin filtrar**.
 *
 * Cuando el filtro de PDV era del servidor, las filas se encogían al elegir una tienda y el
 * desplegable armado sobre ellas se quedaba con esa sola opción: no había vuelta atrás. Ahora
 * la lista completa vive en memoria y estas opciones salen de ahí, así que el problema no
 * existe — y solo se ofrecen las tiendas que de verdad tienen filas en la pestaña.
 */
function opcionesDePdv<P extends { code: string; name: string }>(
  filas: { pdv: P | null }[],
  /** Abiertas filtran por código; Asignadas, por id: cada tabla trae la clave que tiene. */
  claveDe: (pdv: P) => string,
): OpcionFiltro[] {
  const porClave = new Map<string, string>();
  for (const f of filas) {
    if (f.pdv) porClave.set(claveDe(f.pdv), `${f.pdv.code} — ${f.pdv.name}`);
  }
  return [...porClave.entries()]
    .map(([valor, etiqueta]) => ({ valor, etiqueta }))
    .sort((a, b) => a.etiqueta.localeCompare(b.etiqueta, 'es'));
}

/** Opciones simples (horario, slot, estado) a partir de los valores presentes. */
const comoOpciones = (valores: string[]): OpcionFiltro[] =>
  valores.map((v) => ({ valor: v, etiqueta: v }));

export default function Asignaciones() {
  const [campaigns, setCampaigns] = useState<CampaignRow[]>([]);
  const [campaignId, setCampaignId] = useState('');
  const [tab, setTab] = useState<Tab>('abiertas');
  const { roles } = useAuth();
  /**
   * Puerta de la PANTALLA. `editar` es del coordinador; `datos` la ve en lectura (10-set).
   *
   * No es seguridad —eso es la RLS— sino encuadre: el servidor pide ser el coordinador DESIGNADO de
   * la campaña o estar en `campaign_staff` (`manages_campaign`) para asignar, reemplazar, retirar y
   * desasignar, y el validador no es ninguna de las dos cosas. Dibujarle esos botones sería
   * ofrecerle un rechazo, que es la trampa que este archivo existe para evitar.
   */
  const puedeAccionar = puedeEditar('asignaciones', roles);
  const solapas = TAB_LABELS.filter((t) => puedeAccionar || !t.soloEscritura);

  /**
   * Solo las vigentes este mes y el anterior: el desplegable con 11 opciones donde 5 sirven es la principal
   * fuente de asignaciones a la campaña equivocada. El mes anterior entra porque se regulariza en los
   * primeros días del siguiente (el 1-nov desapareció Vital de octubre con jornadas por regularizar).
   */
  useEffect(() => {
    listCampaigns({ soloVigentes: true }).then(setCampaigns).catch((e) => console.error(e));
  }, []);

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold mb-4">Asignaciones</h1>
      <label className="block mb-4">
        <span className="text-sm text-gray-600">Campaña</span>
        <select
          className="mt-1 block w-full max-w-md border rounded px-3 py-2"
          value={campaignId}
          onChange={(e) => setCampaignId(e.target.value)}
        >
          <option value="">— Selecciona una campaña —</option>
          {campaigns.map((c) => (
            <option key={c.id} value={c.id}>{c.client} — {c.brand}</option>
          ))}
        </select>
      </label>
      {campaignId && (
        <>
          <div className="flex flex-wrap gap-2 border-b mb-4">
            {solapas.map(({ key, label }) => (
              <button
                key={key}
                onClick={() => setTab(key)}
                className={`px-4 py-2 -mb-px border-b-2 ${
                  tab === key
                    ? 'border-blue-600 text-blue-700 font-medium'
                    : 'border-transparent text-gray-500'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
          {tab === 'abiertas' && <PosicionesAbiertas campaignId={campaignId} />}
          {tab === 'asignadas' && <PosicionesAsignadas campaignId={campaignId} />}
          {/* La comprobacion se repite acá a propósito, igual que en Aprobaciones: esconder la
              solapa no alcanza si el estado quedó en «masiva» por cualquier camino. */}
          {tab === 'masiva' && puedeAccionar && <CargaMasiva campaignId={campaignId} />}
        </>
      )}
    </div>
  );
}

/**
 * Qué dice la línea de una posición abierta sobre su recurso.
 *
 * POR QUÉ HACE FALTA: `assign_position` crea la asignación como `propuesta` y **no cambia
 * `positions.status`** —la posición sigue 'abierta' hasta que el jefe la aprueba—, así que
 * sin esta marca una fila ya asignada se veía idéntica a una libre: el coordinador no sabía
 * si ya la había asignado, y al reintentarlo el servidor le contestaba "La posición ya está
 * ocupada".
 *
 * La marca depende de que EXISTA la asignación, nunca de que se vea el nombre: bajo RLS el
 * embed a `profiles` devuelve `null` (BITACORA §3.7), y atarla al nombre haría ver libre
 * una posición ocupada, que es exactamente el error que esto evita.
 */
function RecursoDeLaPosicion({ asignacion }: { asignacion: OpenPositionRow['asignacion'] }) {
  if (!asignacion) return <span className="text-gray-500">Sin asignar</span>;
  const w = asignacion.worker;
  return (
    <span className="flex flex-wrap items-center gap-1">
      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs text-amber-800">
        {asignacion.status}
      </span>
      {w ? (
        <>
          <span className="font-medium">{w.firstNames} {w.lastNames}</span>
          {/* El supervisor recibe el nombre sin DNI (completarTrabajadores): sin número, sin rótulo. */}
          {w.dni && <span className="text-gray-500">DNI {w.dni}</span>}
        </>
      ) : (
        <span className="text-gray-500">Recurso no visible</span>
      )}
    </span>
  );
}

/** Proyección de una posición abierta al motor de filtros. */
const filaAbierta = (p: OpenPositionRow): FilaFiltrable => ({
  date: p.date,
  pdvKey: p.pdv?.code ?? '',
  horario: formatoHorario(p.start_time, p.end_time),
  slot: p.slot_index,
  estado: p.asignacion?.status ?? null,
  nombre: p.asignacion?.worker
    ? `${p.asignacion.worker.firstNames} ${p.asignacion.worker.lastNames}`
    : null,
  dni: p.asignacion?.worker?.dni ?? null,
});

function PosicionesAbiertas({ campaignId }: { campaignId: string }) {
  const [positions, setPositions] = useState<OpenPositionRow[]>([]);
  const { roles } = useAuth();
  /** Ver la pantalla es `ver`; accionar en ella es `editar`. Ver el comentario del componente raíz. */
  const puedeAccionar = puedeEditar('asignaciones', roles);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [filtros, setFiltros] = useState<FiltrosTabla>(FILTROS_VACIOS);
  const [modalPosition, setModalPosition] = useState<OpenPositionRow | null>(null);
  const set = (campo: keyof FiltrosTabla) => (v: string) =>
    setFiltros((f) => ({ ...f, [campo]: v }));

  const reload = useCallback(() => {
    setLoading(true);
    setError('');
    listOpenPositions(campaignId)
      .then(setPositions)
      .catch((e) => {
        // Una lectura incompleta LANZA a propósito: es mejor no listar que listar de menos,
        // porque las posiciones que faltaran parecerían ya cubiertas.
        setPositions([]);
        setError(e instanceof Error ? e.message : 'No se pudieron cargar las posiciones.');
      })
      .finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(reload, [reload]);
  useEffect(() => { setAviso(''); setFiltros(FILTROS_VACIOS); }, [campaignId]);

  const conPropuesta = useMemo(() => positions.filter((p) => p.asignacion).length, [positions]);
  const sinRecurso = positions.length - conPropuesta;

  // Las opciones salen de TODAS las posiciones; las filas mostradas, de las que pasan el filtro.
  const filas = useMemo(() => positions.map(filaAbierta), [positions]);
  const visibles = useMemo(
    () => positions.filter((p) => pasaFiltro(filaAbierta(p), filtros)),
    [positions, filtros],
  );
  const opcionesPdv = useMemo(() => opcionesDePdv(positions, (pdv) => pdv.code), [positions]);
  const opcionesEstado: OpcionFiltro[] = useMemo(() => [
    { valor: 'sin', etiqueta: 'Sin asignar' },
    ...opcionesDe(filas, 'estado').map((e) => ({ valor: e, etiqueta: `Con ${e}` })),
  ], [filas]);

  function alAsignar(worker: WorkerSearchResult) {
    setModalPosition(null);
    setAviso(
      `Asignada a ${worker.first_names} ${worker.last_names} (DNI ${worker.dni}). Quedó como ` +
      'propuesta: hasta que el jefe de operaciones la apruebe, esa persona todavía no puede marcar.',
    );
    reload();
  }

  return (
    <div>
      {aviso && <p className="mb-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{aviso}</p>}
      {error && <p className="mb-2 text-sm text-red-600">{error}</p>}
      {loading && <p className="text-gray-500">Cargando posiciones…</p>}
      {!loading && !error && positions.length === 0 && (
        <p className="text-gray-500">No hay posiciones abiertas en esta campaña.</p>
      )}
      {positions.length > 0 && (
        <BarraFiltros
          onLimpiar={() => setFiltros(FILTROS_VACIOS)}
          mostrarLimpiar={hayFiltros(filtros)}
        >
          <FiltroTexto
            etiqueta="Recurso (nombre o DNI)"
            valor={filtros.texto}
            onChange={set('texto')}
            placeholder="Buscar en la columna Recurso"
          />
          <FiltroFecha etiqueta="Desde" valor={filtros.desde} onChange={set('desde')} />
          <FiltroFecha etiqueta="Hasta" valor={filtros.hasta} onChange={set('hasta')} />
          <FiltroSelect
            etiqueta="PDV"
            valor={filtros.pdv}
            onChange={set('pdv')}
            opciones={opcionesPdv}
            todas="Todas las tiendas"
          />
          <FiltroSelect
            etiqueta="Horario"
            valor={filtros.horario}
            onChange={set('horario')}
            opciones={comoOpciones(opcionesDe(filas, 'horario'))}
            todas="Todos los horarios"
          />
          <FiltroSelect
            etiqueta="Slot"
            valor={filtros.slot}
            onChange={set('slot')}
            opciones={comoOpciones(opcionesDe(filas, 'slot'))}
            todas="Todos los slots"
          />
          <FiltroSelect
            etiqueta="Estado"
            valor={filtros.estado}
            onChange={set('estado')}
            opciones={opcionesEstado}
            todas="Con y sin recurso"
          />
        </BarraFiltros>
      )}
      {/* El resumen de lo que está a la vista. Se cuenta sobre `visibles` y no sobre `positions`
          a propósito: si alguien filtra por una tienda, las tarjetas tienen que hablar de esa
          tienda. El total sin filtrar sigue estando en la línea de abajo. */}
      {!loading && visibles.length > 0 && (
        <TarjetasResumen indicadores={[
          { titulo: 'Posiciones a la vista', valor: visibles.length,
            detalle: hayFiltros(filtros) ? `de ${positions.length} abiertas` : undefined },
          { titulo: 'Sin recurso', valor: visibles.filter((p) => p.asignacion === null).length,
            tono: 'malo', detalle: 'nadie asignado todavía' },
          { titulo: 'Con propuesta', valor: visibles.filter((p) => p.asignacion !== null).length,
            tono: 'atencion', detalle: 'falta aprobar' },
          { titulo: 'Tiendas', valor: new Set(visibles.map((p) => p.pdv?.code ?? '—')).size },
          { titulo: 'Fechas', valor: new Set(visibles.map((p) => p.date)).size },
        ]} />
      )}
      {!loading && positions.length > 0 && (
        <p className="mb-2 text-sm text-gray-600">
          {positions.length} posición(es) abierta(s): {sinRecurso} sin recurso
          {conPropuesta > 0
            && `, ${conPropuesta} con propuesta pendiente de aprobación (ya no se pueden asignar)`}
          {/* Un filtro nunca esconde filas en silencio: siempre dice cuántas está mostrando. */}
          {hayFiltros(filtros) && ` · mostrando ${visibles.length} con los filtros puestos`}
        </p>
      )}
      {!loading && positions.length > 0 && visibles.length === 0 && (
        <p className="text-gray-500">Ninguna posición abierta coincide con los filtros.</p>
      )}
      {/* En celular: una tarjeta por posición abierta (sin desplazamiento horizontal). */}
      {!loading && visibles.length > 0 && (
        <div className="space-y-2 lg:hidden">
          {visibles.map((p) => (
            <FilaTarjeta
              key={p.id}
              titulo={p.pdv ? `${p.pdv.code} — ${p.pdv.name}` : 'Sin PDV'}
              estados={<RecursoDeLaPosicion asignacion={p.asignacion} />}
              campos={[
                { etiqueta: 'Fecha', valor: p.date },
                { etiqueta: 'Horario', valor: `${hhmm(p.start_time)}–${hhmm(p.end_time)}` },
                { etiqueta: 'Slot', valor: p.slot_index },
              ]}
              acciones={
                p.asignacion
                  ? <span className="text-xs text-gray-500">Pendiente de aprobación</span>
                  : puedeAccionar ? (
                    <button
                      onClick={() => { setAviso(''); setModalPosition(p); }}
                      className="px-3 py-1 rounded bg-blue-600 text-white"
                    >
                      Asignar
                    </button>
                  ) : <span className="text-xs text-gray-500">Sin cubrir</span>
              }
            />
          ))}
        </div>
      )}
      {!loading && visibles.length > 0 && (
        <div className="hidden overflow-x-auto lg:block">
          <table className="w-full text-sm border rounded">
            <thead>
              <tr className="text-left text-gray-500 bg-gray-50">
                <th className="px-3 py-2">Fecha</th>
                <th className="px-3 py-2">PDV</th>
                <th className="px-3 py-2">Horario</th>
                <th className="px-3 py-2">Slot</th>
                <th className="px-3 py-2">Recurso</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {visibles.map((p) => (
                <tr key={p.id} className="border-t">
                  <td className="px-3 py-2">{p.date}</td>
                  <td className="px-3 py-2">{p.pdv ? `${p.pdv.code} — ${p.pdv.name}` : '—'}</td>
                  <td className="px-3 py-2">{hhmm(p.start_time)}–{hhmm(p.end_time)}</td>
                  <td className="px-3 py-2">{p.slot_index}</td>
                  <td className="px-3 py-2"><RecursoDeLaPosicion asignacion={p.asignacion} /></td>
                  <td className="px-3 py-2 text-right">
                    {p.asignacion ? (
                      <span className="text-xs text-gray-500">Pendiente de aprobación</span>
                    ) : puedeAccionar ? (
                      <button
                        onClick={() => { setAviso(''); setModalPosition(p); }}
                        className="px-3 py-1 rounded bg-blue-600 text-white"
                      >
                        Asignar
                      </button>
                    ) : (
                      <span className="text-xs text-gray-500">Sin cubrir</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {modalPosition && (
        <AsignarModal
          position={modalPosition}
          onClose={() => setModalPosition(null)}
          onAssigned={alAsignar}
        />
      )}
    </div>
  );
}

function AsignarModal({ position, onClose, onAssigned }: {
  position: OpenPositionRow;
  onClose: () => void;
  /** Recibe a quién se asignó: el aviso de la lista lo nombra en vez de decir "listo". */
  onAssigned: (worker: WorkerSearchResult) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<WorkerSearchResult[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => {
      if (query.trim().length < 2) { setResults([]); return; }
      searchWorkers(query).then(setResults).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  async function pick(worker: WorkerSearchResult) {
    setError('');
    setSaving(true);
    try {
      // Feedback anticipado (no es el bloqueo real: la RPC valida en el servidor).
      const existing = await listWorkerAssignments(worker.id, position.date);
      const existingRanges: TimeRange[] = existing.map((a) => ({
        date: a.positions.date,
        startTime: a.positions.start_time,
        endTime: a.positions.end_time,
      }));
      const candidate: TimeRange = {
        date: position.date,
        startTime: position.start_time,
        endTime: position.end_time,
      };
      if (findOverlaps(existingRanges, [candidate]).length > 0) {
        const clash = existing.find((a) => rangesOverlap(
          { date: a.positions.date, startTime: a.positions.start_time, endTime: a.positions.end_time },
          candidate,
        ));
        const name = `${worker.first_names} ${worker.last_names}`;
        setError(clash
          ? `${name} ya tiene turno ${hhmm(clash.positions.start_time)}-${hhmm(clash.positions.end_time)} en ${clash.positions.pdv.name} ese día`
          : `${name} ya tiene un turno que se cruza ese día`);
        return;
      }
      // La RPC `assign_position` valida el cruce en el servidor, fija `assigned_by`
      // y escribe `audit_log` con action = 'proponer_asignacion': no se llama a
      // `logAction` aquí para no duplicar la entrada de auditoría.
      await createAssignment(position.id, worker.id);
      onAssigned(worker);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo asignar. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-1">Asignar trabajador</h2>
        <p className="text-sm text-gray-600 mb-3">
          {position.pdv ? position.pdv.name : 'Sin PDV'} · {position.date}
          {' '}· {hhmm(position.start_time)}–{hhmm(position.end_time)}
        </p>
        <input
          autoFocus
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Buscar por DNI o nombre"
          className="w-full border rounded px-3 py-2 mb-2"
        />
        {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
        <ul className="max-h-60 overflow-y-auto divide-y">
          {results.map((w) => (
            <li key={w.id}>
              <button
                disabled={saving}
                onClick={() => pick(w)}
                className="w-full text-left px-2 py-2 hover:bg-gray-50 disabled:opacity-50"
              >
                <span className="font-medium">{w.first_names} {w.last_names}</span>
                <span className="text-gray-500 ml-2">DNI {w.dni}</span>
              </button>
            </li>
          ))}
          {query.trim().length >= 2 && results.length === 0 && (
            <li className="px-2 py-2 text-sm text-gray-500">Sin resultados</li>
          )}
        </ul>
        <div className="mt-4 text-right">
          <button onClick={onClose} className="px-4 py-2 rounded border">Cancelar</button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Pestaña "Asignadas": posiciones que ya tienen recurso, propuestas y aprobadas.
//
// Absorbió la pestaña "Propuestas" (2026-08-11): allí solo se listaban las `propuesta` de la
// campaña —un subconjunto de esto mismo— y su única acción era "Retirar", que ahora es un
// botón de la fila cuando el estado lo permite. Aprobar sigue siendo del jefe de operaciones,
// en la pantalla Aprobaciones.
// ---------------------------------------------------------------------------

function EstadoAsignacion({ estado }: { estado: string }) {
  const clase = estado === 'aprobada'
    ? 'bg-green-100 text-green-800'
    : 'bg-amber-100 text-amber-800';
  return <span className={`rounded px-2 py-0.5 text-xs ${clase}`}>{estado}</span>;
}

/** Proyección de una posición asignada al motor de filtros. */
const filaAsignada = (r: AssignedPositionRow): FilaFiltrable => ({
  date: r.date,
  pdvKey: r.pdv?.id ?? '',
  horario: formatoHorario(r.startTime, r.endTime),
  slot: r.slotIndex,
  estado: r.assignmentStatus,
  nombre: r.worker ? `${r.worker.firstNames} ${r.worker.lastNames}` : null,
  dni: r.worker?.dni ?? null,
});

function PosicionesAsignadas({ campaignId }: { campaignId: string }) {
  const [rows, setRows] = useState<AssignedPositionRow[]>([]);
  const { roles } = useAuth();
  /** Ver la pantalla es `ver`; accionar en ella es `editar`. Ver el comentario del componente raíz. */
  const puedeAccionar = puedeEditar('asignaciones', roles);
  const [filtros, setFiltros] = useState<FiltrosTabla>(FILTROS_VACIOS);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [aviso, setAviso] = useState('');
  const [retirando, setRetirando] = useState('');
  /** La fila que se esta desasignando, con su motivo. `null` = la ventana esta cerrada. */
  const [desasignando, setDesasignando] = useState<AssignedPositionRow | null>(null);
  const [motivoDesasignar, setMotivoDesasignar] = useState('');
  const [desasignandoAhora, setDesasignandoAhora] = useState(false);
  const [modalRow, setModalRow] = useState<AssignedPositionRow | null>(null);
  const set = (campo: keyof FiltrosTabla) => (v: string) =>
    setFiltros((f) => ({ ...f, [campo]: v }));

  // Se lee la campaña completa (la consulta pagina) y se filtra en memoria: los seis filtros
  // responden igual de rápido y no hay dos caminos distintos. Ver `domain/tableFilters.ts`.
  const reload = useCallback(() => {
    setLoading(true);
    setError('');
    listAssignedPositions(campaignId)
      .then(setRows)
      .catch((e) => {
        setRows([]);
        setError(e instanceof Error ? e.message : 'No se pudieron cargar las asignaciones.');
      })
      .finally(() => setLoading(false));
  }, [campaignId]);

  useEffect(reload, [reload]);
  useEffect(() => { setAviso(''); setFiltros(FILTROS_VACIOS); }, [campaignId]);

  const conMarcado = useMemo(() => rows.filter((r) => r.marcas > 0).length, [rows]);
  const propuestas = useMemo(
    () => rows.filter((r) => r.assignmentStatus === 'propuesta').length,
    [rows],
  );

  const filas = useMemo(() => rows.map(filaAsignada), [rows]);
  const visibles = useMemo(
    () => rows.filter((r) => pasaFiltro(filaAsignada(r), filtros)),
    [rows, filtros],
  );
  const opcionesPdv = useMemo(() => opcionesDePdv(rows, (pdv) => pdv.id), [rows]);

  function alCambiar(nuevoDni: string) {
    setModalRow(null);
    setAviso(
      `Cambio registrado. El reemplazo (DNI ${nuevoDni}) quedó como propuesta: hasta que el ` +
      'jefe de operaciones la apruebe, ese turno no tiene persona activa.',
    );
    reload();
  }

  /**
   * Retira una propuesta (lo que hacía la pestaña "Propuestas"). Solo se ofrece en estado
   * `propuesta`: la política de la 0002 permite el borrado únicamente ahí, y una aprobada se
   * cambia con "Cambiar recurso", que deja rastro en vez de borrarlo.
   */

  /**
   * Desasigna el turno de la ventana abierta.
   *
   * El motivo es obligatorio del lado del servidor tambien; se valida aca para no hacer el viaje.
   * Si la RPC rechaza —evidencia colgando, periodo liquidado— su mensaje se muestra tal cual: dice
   * cuantas marcas, canjes, cierres y visitas hay, y que hacer en su lugar.
   */
  async function confirmarDesasignar() {
    if (!desasignando) return;
    if (motivoDesasignar.trim() === '') {
      setError('Escribe el motivo: es lo que explica por qué se liberó el turno.');
      return;
    }
    setDesasignandoAhora(true);
    setError('');
    setAviso('');
    try {
      await desasignarTurno(desasignando.assignmentId, motivoDesasignar.trim());
      setAviso(`Turno liberado: ${desasignando.date} · `
        + `${desasignando.pdv?.name ?? 'sin tienda'}. La posición volvió a estar abierta.`);
      setDesasignando(null);
      setMotivoDesasignar('');
      reload();
    } catch (e) {
      // El mensaje del servidor se muestra TAL CUAL: dice cuantas marcas, canjes, cierres y
      // visitas hay colgando, y que hacer en su lugar. Traducirlo a «no se pudo» tira lo util.
      setError(e instanceof Error ? e.message : 'No se pudo desasignar.');
    } finally {
      setDesasignandoAhora(false);
    }
  }
  async function retirar(row: AssignedPositionRow) {
    setError('');
    setAviso('');
    setRetirando(row.assignmentId);
    const quien = row.worker
      ? `${row.worker.firstNames} ${row.worker.lastNames}`
      : 'la persona propuesta';
    try {
      await removeProposal(row.assignmentId);
      await logAction('retirar_propuesta', 'assignments', row.assignmentId, {});
      setAviso(`Se retiró la propuesta de ${quien}. Esa posición volvió a quedar libre.`);
      reload();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo retirar la propuesta.');
    } finally {
      setRetirando('');
    }
  }

  return (
    <div>
      {rows.length > 0 && (
        <BarraFiltros
          onLimpiar={() => setFiltros(FILTROS_VACIOS)}
          mostrarLimpiar={hayFiltros(filtros)}
        >
          <FiltroTexto
            etiqueta="Trabajador (nombre o DNI)"
            valor={filtros.texto}
            onChange={set('texto')}
            placeholder="Buscar por nombre o DNI"
          />
          <FiltroFecha etiqueta="Desde" valor={filtros.desde} onChange={set('desde')} />
          <FiltroFecha etiqueta="Hasta" valor={filtros.hasta} onChange={set('hasta')} />
          <FiltroSelect
            etiqueta="PDV"
            valor={filtros.pdv}
            onChange={set('pdv')}
            opciones={opcionesPdv}
            todas="Todas las tiendas"
          />
          <FiltroSelect
            etiqueta="Horario"
            valor={filtros.horario}
            onChange={set('horario')}
            opciones={comoOpciones(opcionesDe(filas, 'horario'))}
            todas="Todos los horarios"
          />
          <FiltroSelect
            etiqueta="Estado"
            valor={filtros.estado}
            onChange={set('estado')}
            opciones={comoOpciones(opcionesDe(filas, 'estado'))}
            todas="Propuestas y aprobadas"
          />
        </BarraFiltros>
      )}

      {aviso && <p className="mb-3 rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-800">{aviso}</p>}
      {error && <p className="mb-2 text-sm text-red-600">{error}</p>}
      {loading && <p className="text-gray-500">Cargando asignaciones…</p>}
      {!loading && rows.length === 0 && (
        <p className="text-gray-500">Esta campaña todavía no tiene posiciones asignadas.</p>
      )}

      {!loading && rows.length > 0 && (
        <>
          {/* «Se puede desasignar» es la misma regla que enciende el botón de la fila —aprobada,
              sin marcas y con la posición abierta—, así que la tarjeta dice cuántos turnos todavía
              se pueden mover. Es el número que se busca cuando el cliente cambia la programación. */}
          <TarjetasResumen indicadores={[
            { titulo: 'Turnos a la vista', valor: visibles.length,
              detalle: hayFiltros(filtros) ? `de ${rows.length} asignados` : undefined },
            { titulo: 'En propuesta',
              valor: visibles.filter((r) => r.assignmentStatus === 'propuesta').length,
              tono: 'atencion', detalle: 'falta aprobar' },
            { titulo: 'Con asistencia', valor: visibles.filter((r) => r.marcas > 0).length,
              detalle: 'ya se ejecutaron' },
            { titulo: 'Se puede desasignar',
              valor: visibles.filter(
                (r) => r.assignmentStatus === 'aprobada' && r.marcas === 0 && !r.posicionCerrada,
              ).length },
            { titulo: 'Personas',
              valor: new Set(visibles.map((r) => r.worker?.id ?? r.assignmentId)).size },
            { titulo: 'Tiendas', valor: new Set(visibles.map((r) => r.pdv?.id ?? '—')).size },
          ]} />

          <p className="mb-2 text-sm text-gray-600">
            {rows.length} posición(es) con recurso
            {propuestas > 0 && `, ${propuestas} en propuesta (falta que el jefe de operaciones apruebe)`}
            {conMarcado > 0 && `, ${conMarcado} con asistencia ya marcada (no se puede cambiar la persona)`}
            {/* Un filtro nunca esconde filas en silencio: siempre dice cuántas está mostrando. */}
            {hayFiltros(filtros) && ` · mostrando ${visibles.length} con los filtros puestos`}
          </p>

          {visibles.length === 0 && (
            <p className="text-gray-500">Ninguna asignación coincide con los filtros.</p>
          )}

          {/* En celular: una tarjeta por posición asignada (sin desplazamiento horizontal). */}
          <div className="space-y-2 lg:hidden">
            {visibles.map((r) => (
              <FilaTarjeta
                key={r.assignmentId}
                titulo={r.worker ? `${r.worker.firstNames} ${r.worker.lastNames}` : 'Sin trabajador'}
                // El supervisor de ruta recibe el nombre sin DNI (0218): sin DNI, sin subtítulo.
                subtitulo={r.worker?.dni ? `DNI ${r.worker.dni}` : undefined}
                estados={
                  <>
                    <EstadoAsignacion estado={r.assignmentStatus} />
                    {r.marcas > 0 && (
                      <span className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-800">
                        {r.marcas === 1 ? '1 marca' : `${r.marcas} marcas`}
                      </span>
                    )}
                    {r.posicionCerrada && (
                      <span className="rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-800">cerrada</span>
                    )}
                  </>
                }
                campos={[
                  { etiqueta: 'Fecha', valor: r.date },
                  { etiqueta: 'PDV', valor: r.pdv ? `${r.pdv.code} — ${r.pdv.name}` : '—' },
                  { etiqueta: 'Horario', valor: `${hhmm(r.startTime)}–${hhmm(r.endTime)}` },
                  { etiqueta: 'Slot', valor: r.slotIndex },
                ]}
                acciones={!puedeAccionar ? null : (
                  <>
                    <button
                      onClick={() => { setAviso(''); setModalRow(r); }}
                      className="px-3 py-1 rounded border border-blue-600 text-blue-700"
                    >
                      Cambiar recurso
                    </button>
                    {r.assignmentStatus === 'propuesta' && (
                      <button
                        disabled={retirando === r.assignmentId}
                        onClick={() => retirar(r)}
                        className="px-3 py-1 rounded border border-red-600 text-red-600 disabled:opacity-50"
                      >
                        {retirando === r.assignmentId ? 'Retirando…' : 'Retirar'}
                      </button>
                    )}
                  </>
                )}
              />
            ))}
          </div>

          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full text-sm border rounded">
              <thead>
                <tr className="text-left text-gray-500 bg-gray-50">
                  <th className="px-3 py-2">Fecha</th>
                  <th className="px-3 py-2">PDV</th>
                  <th className="px-3 py-2">Horario</th>
                  <th className="px-3 py-2">Trabajador</th>
                  <th className="px-3 py-2">DNI</th>
                  <th className="px-3 py-2">Estado</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {visibles.map((r) => (
                  <tr key={r.assignmentId} className="border-t">
                    <td className="px-3 py-2">{r.date}</td>
                    <td className="px-3 py-2">{r.pdv ? `${r.pdv.code} — ${r.pdv.name}` : '—'}</td>
                    <td className="px-3 py-2">{hhmm(r.startTime)}–{hhmm(r.endTime)}</td>
                    <td className="px-3 py-2">
                      {r.worker ? `${r.worker.firstNames} ${r.worker.lastNames}` : 'Sin trabajador'}
                    </td>
                    <td className="px-3 py-2">{r.worker?.dni || '—'}</td>
                    <td className="px-3 py-2">
                      <EstadoAsignacion estado={r.assignmentStatus} />
                      {r.marcas > 0 && (
                        <span className="ml-1 rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-800">
                          {r.marcas === 1 ? '1 marca' : `${r.marcas} marcas`}
                        </span>
                      )}
                      {r.posicionCerrada && (
                        <span className="ml-1 rounded bg-gray-200 px-2 py-0.5 text-xs text-gray-800">cerrada</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-right">
                      {/* En lectura la celda queda vacía en vez de con botones apagados: un botón
                          gris invita a averiguar por qué está gris. */}
                      {puedeAccionar && (
                      <button
                        onClick={() => { setAviso(''); setModalRow(r); }}
                        className="px-3 py-1 rounded border border-blue-600 text-blue-700"
                      >
                        Cambiar recurso
                      </button>
                      )}
                      {puedeAccionar && r.assignmentStatus === 'propuesta' && (
                        <button
                          disabled={retirando === r.assignmentId}
                          onClick={() => retirar(r)}
                          className="ml-2 px-3 py-1 rounded border border-red-600 text-red-600 disabled:opacity-50"
                        >
                          {retirando === r.assignmentId ? 'Retirando…' : 'Retirar'}
                        </button>
                      )}
                      {/*
                        «Retirar» es para una PROPUESTA que todavia no se aprobo; «Desasignar» es
                        para una APROBADA que todavia no se ejecuto. No son lo mismo y por eso son
                        dos botones: retirar deshace un pedido, desasignar deshace una decision.

                        Se ofrece solo si no hay marcas y la posicion no esta cerrada, que es lo
                        que la fila ya sabe. La RPC vuelve a verificar eso y ademas los canjes, los
                        cierres y las visitas de levantamiento —que la fila no trae—, asi que si
                        rechaza, su mensaje dice cuantas hay de cada tipo y se muestra tal cual.
                      */}
                      {puedeAccionar && r.assignmentStatus === 'aprobada' && r.marcas === 0
                        && !r.posicionCerrada && (
                        <button
                          onClick={() => { setAviso(''); setMotivoDesasignar(''); setDesasignando(r); }}
                          className="ml-2 px-3 py-1 rounded border border-amber-600 text-amber-700"
                        >
                          Desasignar
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* La ventana de desasignar. Es reversible —se puede volver a asignar— asi que no lleva el
          tono de alarma del borrado; lo que si lleva es el motivo obligatorio y decir que la
          posicion queda libre, que es la consecuencia que importa para la programacion. */}
      {desasignando && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl">
            <header className="border-b border-gray-200 px-5 py-4">
              <h2 className="text-base font-bold text-gray-900">Desasignar este turno</h2>
              <p className="mt-0.5 text-sm text-gray-700">
                {desasignando.date} · {desasignando.pdv?.name ?? 'sin tienda'}
                {desasignando.worker
                  ? ` · ${desasignando.worker.firstNames} ${desasignando.worker.lastNames}`
                  : ''}
              </p>
            </header>
            <div className="space-y-3 px-5 py-4">
              <p className="rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
                La persona queda sin este turno y <strong>la posición vuelve a estar abierta</strong>,
                así que se puede asignar a alguien más. No se borra nada: la asignación queda como
                rechazada, con tu nombre y el motivo.
              </p>
              <label className="block text-sm">
                <span className="block font-medium text-gray-900">Motivo</span>
                <input
                  type="text" value={motivoDesasignar} autoFocus
                  onChange={(e) => setMotivoDesasignar(e.target.value)}
                  placeholder="Avisó que no puede ir"
                  className="mt-1 w-full rounded border border-gray-300 px-2 py-1"
                />
              </label>
            </div>
            <footer className="flex items-center justify-end gap-2 border-t border-gray-200 px-5 py-3">
              <button type="button" onClick={() => setDesasignando(null)}
                className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-700">
                Cancelar
              </button>
              <button type="button" onClick={() => void confirmarDesasignar()}
                disabled={desasignandoAhora}
                className="rounded bg-amber-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
                {desasignandoAhora ? 'Liberando…' : 'Desasignar y liberar la posición'}
              </button>
            </footer>
          </div>
        </div>
      )}

      {modalRow && (
        <CambiarRecursoModal
          row={modalRow}
          onClose={() => setModalRow(null)}
          onChanged={alCambiar}
        />
      )}
    </div>
  );
}

/**
 * Cambio de recurso sobre una posición ya asignada.
 *
 * Tres momentos, a propósito separados:
 * 1. Si la posición no se puede tocar (asistencia marcada o periodo cerrado) el modal NO
 *    muestra la búsqueda: muestra por qué y cuál es el camino correcto. La misma regla la
 *    aplica el servidor (`replace_assignment`, 0018); esto solo evita el viaje en vano.
 * 2. Búsqueda por DNI o nombre con el mismo `search_workers` de la asignación normal, y la
 *    misma comprobación anticipada de cruce de horario.
 * 3. Confirmación con los avisos: lo que pasa después de confirmar —sobre todo que un turno
 *    aprobado se queda sin persona activa hasta que el jefe apruebe— se lee ANTES.
 */
function CambiarRecursoModal({ row, onClose, onChanged }: {
  row: AssignedPositionRow;
  onClose: () => void;
  onChanged: (nuevoDni: string) => void;
}) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<WorkerSearchResult[]>([]);
  const [elegido, setElegido] = useState<WorkerSearchResult | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);

  const veredicto = puedeCambiarRecurso({
    estado: row.assignmentStatus,
    marcas: row.marcas,
    posicionCerrada: row.posicionCerrada,
  });
  const actual = row.worker ? `${row.worker.firstNames} ${row.worker.lastNames}` : 'el recurso actual';

  useEffect(() => {
    const t = setTimeout(() => {
      if (query.trim().length < 2) { setResults([]); return; }
      searchWorkers(query).then(setResults).catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  async function elegir(worker: WorkerSearchResult) {
    setError('');
    if (worker.id === row.worker?.id) {
      setError(`${worker.first_names} ${worker.last_names} ya es quien está asignado.`);
      return;
    }
    setSaving(true);
    try {
      // Feedback anticipado (el bloqueo real lo hace la RPC en el servidor). Se excluye la
      // posición que se está reemplazando: el turno que va a quedar libre no es un cruce.
      const existing = await listWorkerAssignments(worker.id, row.date);
      const existingRanges: TimeRange[] = existing
        .filter((a) => a.positions.id !== row.positionId)
        .map((a) => ({
          date: a.positions.date,
          startTime: a.positions.start_time,
          endTime: a.positions.end_time,
        }));
      const candidate: TimeRange = {
        date: row.date, startTime: row.startTime, endTime: row.endTime,
      };
      if (findOverlaps(existingRanges, [candidate]).length > 0) {
        const clash = existing.find((a) => a.positions.id !== row.positionId && rangesOverlap(
          { date: a.positions.date, startTime: a.positions.start_time, endTime: a.positions.end_time },
          candidate,
        ));
        const name = `${worker.first_names} ${worker.last_names}`;
        setError(clash
          ? `${name} ya tiene turno ${hhmm(clash.positions.start_time)}-${hhmm(clash.positions.end_time)} en ${clash.positions.pdv.name} ese día`
          : `${name} ya tiene un turno que se cruza ese día`);
        return;
      }
      setElegido(worker);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo verificar el horario. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  }

  async function confirmar() {
    if (!elegido) return;
    setError('');
    setSaving(true);
    try {
      // La RPC audita el reemplazo (action = 'reemplazar_asignacion'): no se llama a
      // `logAction` aquí para no duplicar la entrada de auditoría.
      await replaceAssignment(row.assignmentId, elegido.dni);
      onChanged(elegido.dni);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cambiar el recurso. Intenta de nuevo.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-5 max-h-[90vh] overflow-y-auto">
        <h2 className="text-lg font-semibold mb-1">Cambiar recurso</h2>
        <p className="text-sm text-gray-600 mb-3">
          {row.pdv ? row.pdv.name : 'Sin PDV'} · {row.date} · {hhmm(row.startTime)}–{hhmm(row.endTime)}
          <br />
          Hoy está asignado <span className="font-medium">{actual}</span>
          {row.worker && ` (DNI ${row.worker.dni})`} · asignación {row.assignmentStatus}.
        </p>

        {!veredicto.permitido && (
          <>
            <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium">No se puede cambiar la persona en esta posición.</p>
              <p className="mt-1">{veredicto.motivo}</p>
              {veredicto.salida && <p className="mt-2">{veredicto.salida}</p>}
            </div>
            <div className="text-right">
              <button onClick={onClose} className="px-4 py-2 rounded border">Entendido</button>
            </div>
          </>
        )}

        {veredicto.permitido && !elegido && (
          <>
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por DNI o nombre"
              className="w-full border rounded px-3 py-2 mb-2"
            />
            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
            <ul className="max-h-60 overflow-y-auto divide-y">
              {results.map((w) => (
                <li key={w.id}>
                  <button
                    disabled={saving}
                    onClick={() => elegir(w)}
                    className="w-full text-left px-2 py-2 hover:bg-gray-50 disabled:opacity-50"
                  >
                    <span className="font-medium">{w.first_names} {w.last_names}</span>
                    <span className="text-gray-500 ml-2">DNI {w.dni}</span>
                  </button>
                </li>
              ))}
              {query.trim().length >= 2 && results.length === 0 && (
                <li className="px-2 py-2 text-sm text-gray-500">Sin resultados</li>
              )}
            </ul>
            <div className="mt-4 text-right">
              <button onClick={onClose} className="px-4 py-2 rounded border">Cancelar</button>
            </div>
          </>
        )}

        {veredicto.permitido && elegido && (
          <>
            <div className="mb-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
              <p className="font-medium">Antes de confirmar, lee qué va a pasar:</p>
              <ul className="mt-1 list-disc pl-5 space-y-1">
                {avisosDeReemplazo({
                  estado: row.assignmentStatus,
                  recursoActual: actual,
                  recursoNuevo: `${elegido.first_names} ${elegido.last_names}`,
                }).map((a) => <li key={a}>{a}</li>)}
              </ul>
            </div>
            {error && <p className="text-sm text-red-600 mb-2">{error}</p>}
            <div className="flex flex-wrap justify-end gap-2">
              <button
                onClick={() => { setElegido(null); setError(''); }}
                disabled={saving}
                className="px-4 py-2 rounded border disabled:opacity-50"
              >
                Elegir a otra persona
              </button>
              <button
                onClick={confirmar}
                disabled={saving}
                className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
              >
                {saving ? 'Cambiando…' : 'Confirmar cambio'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function CargaMasiva({ campaignId }: { campaignId: string }) {
  const [parsed, setParsed] = useState<ParseResult<ParsedAssignmentRow> | null>(null);
  const [results, setResults] = useState<BulkAssignResult[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // reset al cambiar de campaña
  useEffect(() => {
    setParsed(null);
    setResults(null);
    setError('');
  }, [campaignId]);

  async function downloadTemplate() {
    setError('');
    setBusy(true);
    try {
      const positions = await listPositions({ campaignId, status: 'abierta' });
      if (positions.length === 0) {
        setError('La campaña no tiene posiciones abiertas.');
        return;
      }
      const template = buildAssignmentsTemplate(positions.map((p) => ({
        positionId: p.id,
        date: p.date,
        pdvCode: p.pdv.code,
        pdvName: p.pdv.name,
        startTime: p.start_time.slice(0, 5),
        endTime: p.end_time.slice(0, 5),
      })));
      const blob = new Blob([template], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'plantilla-asignaciones.xlsx';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo generar la plantilla.');
    } finally {
      setBusy(false);
    }
  }

  async function onFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setResults(null);
    setError('');
    const buffer = await file.arrayBuffer();
    setParsed(parseAssignmentsFile(buffer));
    e.target.value = '';
  }

  async function confirmar() {
    if (!parsed || parsed.rows.length === 0) return;
    setBusy(true);
    setError('');
    try {
      const res = await bulkAssign(parsed.rows.map((r) => ({
        position_id: r.positionId,
        worker_dni: r.dni,
      })));
      setResults(res);
      const ok = res.filter((r) => r.ok).length;
      await logAction('carga_masiva_asignaciones', 'assignments', null, {
        campaign_id: campaignId,
        total: res.length,
        asignadas: ok,
        rechazadas: res.length - ok,
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Error al ejecutar la carga masiva');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Reporte descargable que exige la spec §9 ("qué filas entraron y cuáles no, y por qué").
   * Mismo patrón que el importador de PDV (Task 13): un .txt con los errores de formato de
   * `ParseResult.errors` y el resultado por fila devuelto por la RPC `bulk_assign`.
   */
  function downloadDetail() {
    if (!parsed) return;
    const now = new Date();
    const stamp = now.toISOString().slice(0, 19).replace(/[:T]/g, '-');
    const okRows = results ? results.filter((r) => r.ok).length : 0;
    const lines: string[] = [
      'Detalle de carga masiva de asignaciones',
      `Campaña: ${campaignId}`,
      `Generado: ${now.toLocaleString('es-PE')}`,
      '',
      `Filas leídas del archivo: ${parsed.rows.length + parsed.errors.length}`,
      `Rechazadas por formato: ${parsed.errors.length}`,
      results
        ? `Enviadas al servidor: ${results.length} (asignadas ${okRows}, rechazadas ${results.length - okRows})`
        : 'Enviadas al servidor: 0 (la carga aún no se confirmó)',
      '',
      'ERRORES DE FORMATO (no se enviaron al servidor)',
    ];
    if (parsed.errors.length === 0) {
      lines.push('  (ninguno)');
    } else {
      for (const er of parsed.errors) lines.push(`  Fila ${er.rowNumber}: ${er.message}`);
    }
    lines.push('', 'RESULTADO POR FILA', 'Posicion\tDNI\tOK\tError');
    if (results) {
      for (const r of results) {
        lines.push(`${r.position_id}\t${r.worker_dni}\t${r.ok ? 'SI' : 'NO'}\t${r.ok ? '' : r.error ?? 'error desconocido'}`);
      }
    } else {
      for (const r of parsed.rows) lines.push(`${r.positionId}\t${r.dni}\t-\tsin enviar`);
    }
    const blob = new Blob([lines.join('\r\n')], { type: 'text/plain;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `detalle-carga-asignaciones-${stamp}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  }

  const okCount = results ? results.filter((r) => r.ok).length : 0;

  return (
    <div className="max-w-3xl">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          onClick={downloadTemplate}
          disabled={busy}
          className="px-4 py-2 rounded bg-blue-600 text-white disabled:opacity-50"
        >
          Descargar plantilla
        </button>
        <label className="px-4 py-2 rounded border cursor-pointer">
          Subir plantilla llena
          <input type="file" accept=".xlsx" className="hidden" onChange={onFile} />
        </label>
      </div>
      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}

      {parsed && !results && (
        <div className="mb-4">
          <h3 className="font-medium mb-2">
            Vista previa: {parsed.rows.length} fila(s) válida(s), {parsed.errors.length} error(es) de formato
          </h3>
          {parsed.errors.length > 0 && (
            <>
              <ul className="mb-3 text-sm text-red-600 list-disc pl-5">
                {parsed.errors.map((er) => (
                  <li key={er.rowNumber}>Fila {er.rowNumber}: {er.message}</li>
                ))}
              </ul>
              <button onClick={downloadDetail} className="mb-3 px-4 py-2 rounded border">
                Descargar detalle
              </button>
            </>
          )}
          {parsed.rows.length > 0 && (
            <>
              {/* En celular: una tarjeta por fila del archivo (sin desplazamiento horizontal). */}
              <div className="mb-3 space-y-2 lg:hidden">
                {parsed.rows.map((r) => (
                  <FilaTarjeta
                    key={r.rowNumber}
                    titulo={`DNI ${r.dni}`}
                    campos={[
                      { etiqueta: 'Fila', valor: r.rowNumber },
                      {
                        etiqueta: 'Posición ID',
                        valor: <span className="font-mono text-xs">{r.positionId}</span>,
                      },
                    ]}
                  />
                ))}
              </div>
              <div className="mb-3 hidden overflow-x-auto lg:block">
                <table className="w-full text-sm border rounded">
                  <thead>
                    <tr className="text-left text-gray-500 bg-gray-50">
                      <th className="px-3 py-2">Fila</th>
                      <th className="px-3 py-2">Posición ID</th>
                      <th className="px-3 py-2">DNI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsed.rows.map((r) => (
                      <tr key={r.rowNumber} className="border-t">
                        <td className="px-3 py-2">{r.rowNumber}</td>
                        <td className="px-3 py-2 font-mono text-xs">{r.positionId}</td>
                        <td className="px-3 py-2">{r.dni}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <button
                onClick={confirmar}
                disabled={busy}
                className="px-4 py-2 rounded bg-green-600 text-white disabled:opacity-50"
              >
                Confirmar carga ({parsed.rows.length})
              </button>
            </>
          )}
        </div>
      )}

      {results && (
        <div>
          <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
            <h3 className="font-medium">
              {okCount} asignadas, {results.length - okCount} rechazadas
              {parsed && parsed.errors.length > 0
                ? `, ${parsed.errors.length} descartadas por formato`
                : ''}
            </h3>
            <button onClick={downloadDetail} className="px-4 py-2 rounded border">
              Descargar detalle
            </button>
          </div>
          {/* En celular: una tarjeta por resultado (sin desplazamiento horizontal). */}
          <div className="space-y-2 lg:hidden">
            {results.map((r, i) => (
              <FilaTarjeta
                key={i}
                titulo={`DNI ${r.worker_dni}`}
                estados={
                  <span className={r.ok ? 'text-green-700' : 'text-red-600'}>
                    {r.ok ? 'Asignada' : r.error}
                  </span>
                }
                campos={[
                  {
                    etiqueta: 'Posición ID',
                    valor: <span className="font-mono text-xs">{r.position_id}</span>,
                  },
                ]}
              />
            ))}
          </div>
          <div className="hidden overflow-x-auto lg:block">
            <table className="w-full text-sm border rounded">
              <thead>
                <tr className="text-left text-gray-500 bg-gray-50">
                  <th className="px-3 py-2">DNI</th>
                  <th className="px-3 py-2">Posición ID</th>
                  <th className="px-3 py-2">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {results.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-3 py-2">{r.worker_dni}</td>
                    <td className="px-3 py-2 font-mono text-xs">{r.position_id}</td>
                    <td className={`px-3 py-2 ${r.ok ? 'text-green-700' : 'text-red-600'}`}>
                      {r.ok ? 'Asignada' : r.error}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
